import { CSSProperties, useLayoutEffect, useRef, useState } from 'react';


const EMPTY_HEIGHT = 56;
const MIN_HEIGHT = EMPTY_HEIGHT;
const MAX_HEIGHT = 250;
const CONTENT_HEIGHT_BUFFER = 10;

interface Props {
    style?: CSSProperties;
    notice: string | undefined;
    onNoticeClicked: () => void;
}

export function NoticePanel({
    style,
    notice,
    onNoticeClicked,
}: Props) {
    const ref = useRef<HTMLTextAreaElement>(null);
    const noteText = notice ?? '';
    const isEmpty = noteText.trim().length === 0;
    const [fontSize, setFontSize] = useState(32);
    const [height, setHeight] = useState(EMPTY_HEIGHT);

    // Adjust font size based on content
    useLayoutEffect(() => {
        const minFontSize = 16;
        const maxFontSize = 32;

        const el = ref.current;
        if (!el) return;

        if (isEmpty) {
            setHeight(EMPTY_HEIGHT);
            setFontSize(maxFontSize);
            return;
        }

        const resize = () => {
            if (!el) return;
            let size = maxFontSize;
            el.style.fontSize = `${size}px`;
            // Reset height before measuring to avoid stale values from previous renders.
            el.style.height = '0px';
            while (size > minFontSize && el.scrollHeight > MAX_HEIGHT) {
                size--;
                el.style.fontSize = `${size}px`;
            }
            const nextHeight = Math.min(Math.max(Math.ceil(el.scrollHeight + CONTENT_HEIGHT_BUFFER), MIN_HEIGHT), MAX_HEIGHT);
            setHeight(nextHeight);
            setFontSize(size);
        };

        resize();
    }, [isEmpty, noteText]);

    if (isEmpty) {
        return (
            <div
                className='tasks-notice-panel tasks-notice-panel--empty'
                style={{
                    border: '4px solid #04aa6d',
                    height: EMPTY_HEIGHT,
                    minHeight: EMPTY_HEIGHT,
                    maxHeight: EMPTY_HEIGHT,
                    padding: '0 16px',
                    fontSize: 24,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    ...style,
                }}
                role='button'
                tabIndex={0}
                onClick={onNoticeClicked}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNoticeClicked();
                    }
                }}
            >
                Click to add note...
            </div>
        );
    }


    return (
        <textarea
            className='tasks-notice-panel'
            ref={ref}
            style={{
                border: '4px solid #04aa6d',
                padding: '6px 16px',
                fontSize,
                lineHeight: 1.1,
                height,
                minHeight: MIN_HEIGHT,
                maxHeight: MAX_HEIGHT,
                overflowY: height === MAX_HEIGHT ? 'auto' : 'hidden',
                ...style,
            }}
            rows={1}
            readOnly
            value={noteText}
            onClick={onNoticeClicked}
        />
    );
}
